import { yupResolver } from '@hookform/resolvers/yup';
import {
    ArrowSquareOut,
    CloudArrowUp,
    Key,
    Note,
    ShieldCheck,
    WarningCircle,
} from '@phosphor-icons/react';
import { Button, Label, TextInput, Tooltip } from 'flowbite-react';
import React, { useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { IIAPConnectResponse } from '../../../../interfaces';
import * as yup from 'yup';
import { APP_PLATFORM_LOGO_MAP } from '../../../../shared/constants';
import type { GoogleIAPFormData } from '../interface';
import AnimatedModal from '../../../../components/AnimatedModal';

interface GoogleIAPModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Calls the connect API and returns the response */
    onConnect: (formData: GoogleIAPFormData) => Promise<IIAPConnectResponse>;
    /** Called after the flow completes */
    onSuccess: (selectedAppIds: string[]) => void;
}

// Validation schema
const getGoogleIAPSchema = (t: TFunction) => yup.object().shape({
    service_account_json: yup
        .mixed()
        .required(t('configurations.integrations.googleIap.errors.jsonRequired')),
    email: yup
        .string()
        .required(t('configurations.integrations.googleIap.errors.emailRequired')),
    bucket_name: yup
        .string()
        .required(t('configurations.integrations.googleIap.errors.bucketRequired'))
        .test(
            'valid-bucket-format',
            'Must be a valid Cloud Storage URI like gs://{bucketname}/earning',
            (value) => {
                const trimmed = value?.trim();
                if (!trimmed) return false;
                const re = /^gs:\/\/[^/]+\/earnings?(?:\/.*)?$/;
                return re.test(trimmed);
            }
        ),
});

const GoogleIAPModal: React.FC<GoogleIAPModalProps> = ({
    isOpen,
    onClose,
    onConnect,
    onSuccess,
}) => {
    const { t } = useTranslation();

    // ── Form state ──────────────────────────────────────────────────────────
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectError, setConnectError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
        clearErrors,
    } = useForm<GoogleIAPFormData>({
        resolver: yupResolver(getGoogleIAPSchema(t)) as unknown as Resolver<GoogleIAPFormData>,
        defaultValues: { service_account_json: null, email: '', bucket_name: '' },
    });

    // ── Handlers ────────────────────────────────────────────────────────────
    const handleClose = () => {
        reset();
        setSelectedFile(null);
        setFileError(null);
        setConnectError(null);
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        validateAndSetFile(e.target.files?.[0]);
    };

    const validateAndSetFile = (file?: File) => {
        setFileError(null);
        clearErrors('service_account_json');
        if (!file) { setFileError(t('configurations.integrations.googleIap.errors.jsonSelect')); return; }
        if (!file.name.endsWith('.json')) { setFileError(t('configurations.integrations.googleIap.errors.jsonInvalid')); return; }
        if (file.size > 2 * 1024 * 1024) { setFileError(t('configurations.integrations.googleIap.errors.fileSize')); return; }
        setSelectedFile(file);
        const dt = new DataTransfer();
        dt.items.add(file);
        setValue('service_account_json', dt.files);
    };

    const onFormSubmit = async (data: GoogleIAPFormData) => {
        if (!selectedFile) { setFileError(t('configurations.integrations.googleIap.errors.jsonSelect')); return; }

        const dt = new DataTransfer();
        dt.items.add(selectedFile);
        let bucketName = data.bucket_name?.trim();
        const match = bucketName.match(/^gs:\/\/([^/]+)\/earnings?(?:\/.*)?$/);
        if (match?.[1]) bucketName = match[1];

        setIsConnecting(true);
        setConnectError(null);

        try {
            await onConnect({
                service_account_json: dt.files,
                email: data.email,
                bucket_name: bucketName,
            });
            onSuccess([]);
            handleClose();
        } catch (err: unknown) {
            setConnectError(err instanceof Error ? err.message : t('configurations.integrations.errors.connectGooglePlayIAP', 'Failed to connect. Please try again.'));
        } finally {
            setIsConnecting(false);
        }
    };

    const externalLinks = {
        serviceAccounts: 'https://console.cloud.google.com/iam-admin/serviceaccounts',
        earningReports: 'https://play.google.com/console/u/1/developers',
        supportGuide: '/support#google',
    };

    // ── Modal title ──────────────────────────────────────────────────────────
    const modalTitle = (
        <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <img src={APP_PLATFORM_LOGO_MAP.playStore} alt="Google" className="w-8 h-8" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{t('configurations.integrations.google_iap')}</h3>
                    <Tooltip content={t('configurations.integrations.viewGuide')} placement="right">
                        <Note size={20} weight="duotone" className="text-gray-500 cursor-pointer"
                            onClick={() => window.open(externalLinks.supportGuide, '_blank')} />
                    </Tooltip>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <ShieldCheck size={13} className="text-primary-600 flex-shrink-0" />
                    <p className="text-[11px] text-gray-500">{t('configurations.integrations.secureNoteIap')}</p>
                </div>
            </div>
        </div>
    );

    return (
        <AnimatedModal show={isOpen} onClose={handleClose} size="lg" title={modalTitle}>
            <div className="relative">
                <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
                    {/* File Upload */}
                    <div>
                        <Label htmlFor="service_account_json" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-primary-600" />
                            {t('configurations.integrations.googleIap.serviceAccountKeyLabel')}
                        </Label>
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                            onDrop={(e) => { e.preventDefault(); setIsDragging(false); validateAndSetFile(e.dataTransfer.files?.[0]); }}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl px-4 py-2 text-center cursor-pointer transition-all ${fileError || errors.service_account_json ? 'border-red-500' : ''
                                } ${isDragging ? 'border-primary-500 bg-primary-50' : selectedFile ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                            <CloudArrowUp size={32} className={`mx-auto mb-2 ${selectedFile ? 'text-emerald-600' : 'text-gray-400'}`} />
                            {selectedFile ? (
                                <p className="text-sm font-medium text-emerald-700">{selectedFile.name}</p>
                            ) : (
                                <>
                                    <p className={`text-sm text-gray-600 ${fileError || errors.service_account_json ? 'text-red-500' : ''}`}>
                                        {t('configurations.integrations.dropFile', { ext: 'JSON' })}
                                    </p>
                                    <p className={`text-xs text-gray-400 mt-1 ${fileError || errors.service_account_json ? 'text-red-500' : ''}`}>
                                        {t('configurations.integrations.googleIap.serviceAccountKeyDescription')}
                                    </p>
                                </>
                            )}
                            <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={handleFileChange} className="hidden" />
                        </div>
                        {(fileError || errors.service_account_json) && (
                            <p className="text-sm text-red-600 mt-1">{fileError || (errors.service_account_json?.message as string)}</p>
                        )}
                    </div>

                    {/* Service Account Email */}
                    <div>
                        <Label htmlFor="email" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-primary-600" />
                            {t('configurations.integrations.googleIap.serviceAccountEmailLabel')}
                        </Label>
                        <TextInput
                            id="email"
                            placeholder="e.g. apprevbooster@project-id.iam.gserviceaccount.com"
                            {...register('email')}
                            color={errors.email ? 'failure' : undefined}
                        />
                        {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
                        <a href={externalLinks.serviceAccounts} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 mt-1">
                            <ArrowSquareOut size={12} />
                            Open Google Cloud Console → IAM & Admin → Service Accounts
                        </a>
                    </div>

                    {/* Bucket Name */}
                    <div>
                        <Label htmlFor="bucket_name" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-primary-600" />
                            {t('configurations.integrations.googleIap.bucketNameLabel')}
                        </Label>
                        <TextInput
                            id="bucket_name"
                            placeholder="eg., gs://pubsite_prod_rev_05493956521912004335/earning"
                            {...register('bucket_name')}
                            color={errors.bucket_name ? 'failure' : undefined}
                        />
                        {errors.bucket_name && <p className="text-sm text-red-600 mt-1">{errors.bucket_name.message}</p>}
                        <a href={externalLinks.earningReports} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 mt-1">
                            <ArrowSquareOut size={12} />
                            Play Console → Download Reports → Financial → Earning Reports → "Copy Cloud Storage URI"
                        </a>
                    </div>

                    {/* Note Banner */}
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 leading-relaxed">
                        <p className="font-semibold mb-1">{t('validation.importantNote')}</p>
                        {t('configurations.integrations.googleIapNote')}
                    </div>

                    {/* Connect error */}
                    {connectError && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                            <WarningCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-red-700">{connectError}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <Button color="light" onClick={handleClose} disabled={isConnecting}>
                            {t('common.cancel')}
                        </Button>
                        <Button color="primary" type="submit" disabled={isConnecting} isProcessing={isConnecting}>
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={16} />
                                {t('configurations.integrations.connectServiceAccount')}
                            </div>
                        </Button>
                    </div>
                </form>
            </div>
        </AnimatedModal>
    );
};

export default GoogleIAPModal;
